import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Link2,
  Building2,
  FileText,
  CreditCard,
  Settings as SettingsIcon,
  Sparkles,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  CalendarClock,
  Radar,
  ShoppingBag,
  PlusCircle,
  Search,
  Bell,
  Globe,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/app/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/affiliate", label: "Affiliate Studio", icon: Link2, testid: "nav-affiliate" },
  { to: "/app/schedules", label: "Auto-Publish", icon: CalendarClock, testid: "nav-schedules" },
  { to: "/app/refresh-radar", label: "Refresh Radar", icon: Radar, testid: "nav-refresh-radar" },
  { to: "/app/paapi", label: "PA-API Ops", icon: ShoppingBag, testid: "nav-paapi" },
  { to: "/app/businesses", label: "Businesses", icon: Building2, testid: "nav-businesses" },
  { to: "/app/content", label: "Content Vault", icon: FileText, testid: "nav-content" },
  { to: "/app/subscriptions", label: "Billing", icon: CreditCard, testid: "nav-subscriptions" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const marketTicker = [
  "AMZN Assoc • 4.5% avg",
  "ClickBank • 60d cookie",
  "Impact • SaaS 30% avg",
  "CJ Affiliate • 8% CTR",
  "ShareASale • 5K+ merchants",
  "Rakuten • Retail 12%",
  "Awin • EU strong",
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-neutral-100 relative">
      {/* Ticker bar */}
      <div data-testid="ticker-bar" className="border-b border-slate-800 bg-slate-900/60 backdrop-blur overflow-hidden py-2">
        <div className="ticker-track flex whitespace-nowrap gap-10 text-[11px] font-mono tracking-widest text-sky-300/70 uppercase">
          {[...marketTicker, ...marketTicker].map((t, i) => (
            <span key={i} className="flex items-center gap-3">
              <span className="w-1 h-1 rounded-full bg-sky-400" /> {t}
            </span>
          ))}
        </div>
      </div>

      {/* Top nav */}
      <header className="border-b border-slate-800 sticky top-0 z-30 bg-slate-950/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden text-neutral-300"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-2xl font-medium text-sky-100 tracking-tight">
                Affiliate<span className="text-sky-400">.</span>
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 hidden sm:inline">
                Volume 26 / Q3
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {(user?.role === "admin" || user?.role === "owner") && (
              <Link
                to="/admin"
                data-testid="topbar-btn-admin"
                className="hidden md:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-sky-400 text-sky-200 hover:bg-sky-400 hover:text-slate-950 font-medium transition"
                title="Admin console"
              >
                Admin
              </Link>
            )}
            {user?.role === "owner" && (
              <Link
                to="/owner"
                data-testid="topbar-btn-owner"
                className="hidden md:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-amber-400 text-amber-200 hover:bg-amber-400 hover:text-slate-950 font-medium transition"
                title="Owner console"
              >
                Owner
              </Link>
            )}
            {/* Quick action buttons */}
            <Link
              to="/app/affiliate"
              data-testid="topbar-btn-new"
              className="hidden md:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium transition"
              title="Create new article"
            >
              <PlusCircle className="w-3.5 h-3.5" /> New article
            </Link>
            <Link
              to="/browse"
              data-testid="topbar-btn-browse"
              className="hidden md:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-700 text-neutral-400 hover:text-sky-200 hover:border-sky-400/40 transition"
              title="Open public site"
            >
              <Globe className="w-3.5 h-3.5" /> Public site
            </Link>
            <button
              data-testid="topbar-btn-search"
              onClick={() => navigate("/browse")}
              className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-700 text-neutral-400 hover:text-sky-200 hover:border-sky-400/40 transition"
              title="Search"
            >
              <Search className="w-4 h-4" />
            </button>
            <Link
              to="/app/refresh-radar"
              data-testid="topbar-btn-alerts"
              className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-700 text-neutral-400 hover:text-sky-200 hover:border-sky-400/40 transition relative"
              title="Refresh alerts"
            >
              <Bell className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-sky-400 rounded-full" />
            </Link>

            <div className="hidden md:flex flex-col items-end ml-2 pl-3 border-l border-slate-800">
              <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                Signed in
              </span>
              <span className="text-sm text-sky-100" data-testid="user-email">
                {user?.email}
              </span>
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 text-slate-900 flex items-center justify-center font-serif font-semibold text-sm">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <button
              onClick={handleLogout}
              data-testid="btn-logout"
              className="flex items-center gap-2 text-xs text-neutral-400 hover:text-sky-300 transition ml-1"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 max-w-[1600px] w-full mx-auto">
        {/* Sidebar */}
        <aside
          className={`${
            mobileOpen ? "block" : "hidden"
          } lg:block ${sidebarCollapsed ? "lg:w-20" : "lg:w-64"} w-64 border-r border-slate-800 py-8 px-4 flex-shrink-0 min-h-[calc(100vh-8rem)] transition-all duration-200 relative`}
        >
          <button
            data-testid="sidebar-collapse"
            onClick={() => setSidebarCollapsed((s) => !s)}
            className="hidden lg:flex absolute -right-3 top-8 w-6 h-6 rounded-full bg-slate-900 border border-slate-700 text-neutral-400 hover:text-sky-300 items-center justify-center z-10"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
          </button>
          {!sidebarCollapsed && (
            <div className="mb-6 px-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                — Navigation
              </p>
            </div>
          )}
          <nav className="space-y-1">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `group flex items-center ${sidebarCollapsed ? "justify-center" : "justify-between"} px-3 py-2.5 rounded-md text-sm transition ${
                      isActive
                        ? "bg-sky-400/10 text-sky-200 border-l-2 border-sky-400"
                        : "text-neutral-400 hover:text-sky-100 hover:bg-slate-900 border-l-2 border-transparent"
                    }`
                  }
                  title={sidebarCollapsed ? item.label : ""}
                >
                  <span className="flex items-center gap-3">
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {!sidebarCollapsed && item.label}
                  </span>
                  {!sidebarCollapsed && (
                    <ChevronRight className="w-3 h-3 opacity-40 group-hover:opacity-100 transition" />
                  )}
                </NavLink>
              );
            })}
          </nav>

          {!sidebarCollapsed && (
            <div className="mt-10 mx-3 p-4 rounded-lg border border-sky-500/25 bg-gradient-to-br from-sky-500/8 to-transparent">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-sky-400" />
                <p className="font-mono text-[10px] uppercase tracking-widest text-sky-300/80">
                  Engine
                </p>
              </div>
              <p className="font-serif text-base leading-snug text-sky-100">
                GPT-5.6 Terra
              </p>
              <p className="text-[11px] text-neutral-500 mt-1">
                Autonomous content · Live network detection
              </p>
            </div>
          )}
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <footer className="border-t border-slate-800 py-4 px-6 text-[11px] font-mono uppercase tracking-widest text-slate-600 text-center">
        Universal AI Business Builder · Affiliate Marketing Module · Vol 26.Q3
      </footer>
    </div>
  );
}
